import {
  useEffect,
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import Redo from "@mui/icons-material/Redo";
import Undo from "@mui/icons-material/Undo";
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAppDialog } from "@pepsico/layout";
import { usePagePrefs } from "@pepsico/pref-permissions";
import { useQueryClient } from "@tanstack/react-query";
import { useMap } from "react-map-gl/mapbox";
import { noop } from "es-toolkit";

import { useGetGrowersGeoByLocation } from "api/growers-geo/growers-geo";
import type {
  GrowerGeoResponse,
  PlotGeoResponse,
  RowGeoResponse,
  RowSnipResponse,
  SiteGeoResponse,
  TrialGeoResponse,
} from "api/model";
import {
  getGetPlotsGeoByLocationQueryKey,
  useDeletePlotGeo,
  useGetPlotsGeoByLocation,
  useResetPlotsGeoLayout,
  useUpdatePlotsGeoBulk,
  useUpdatePlotsGeoLayoutBulk,
  type getPlotsGeoByLocationResponse,
} from "api/plots-geo/plots-geo";
import {
  getGetRowsGeoQueryKey,
  useGetRowsGeo,
  useUpdateRowsGeoBulk,
  type getRowsGeoResponse,
} from "api/rows-geo/rows-geo";
import { useGetSitesGeoByLocation } from "api/sites-geo/sites-geo";
import { useGetRowSnips } from "api/snips/snips";
import { useGetTrialsGeoByLocation } from "api/trials-geo/trials-geo";
import {
  fitMapToBoundaries,
  fitMapToBoundary,
} from "components/PlotMap/utils/mapCameraUtils";
import { isPlotMapLayerVisible } from "components/PlotMap/utils/layerPrefs";
import {
  readSelectedGrowerGeoId,
  readSelectedSiteGeoId,
  readSelectedTrialGeoId,
} from "components/PlotMap/utils/selectionPrefs";
import { usePlotMap } from "context/PlotMap";
import { hasBoundaryGeoJson } from "context/PlotMap/boundaryUtils";
import {
  DEFAULT_PLOT_LENGTH_METERS,
  DEFAULT_PLOT_WIDTH_METERS,
} from "context/PlotMap/gridUtils";
import {
  formatTrialPlotSizeInfo,
  getInitialCellSeeds,
  getInitialGridLayout,
  getPlotGridStats,
  hasPlotsGeo,
  hasPlotGridOutsideSiteBoundary,
  planPlotGridEditSave,
} from "context/PlotMap/plotGridGeoUtils";
import { getApiErrorMessage } from "utils";

import GoToBoundsPrompt from "./GoToBoundsPrompt";
import PlotTrialSection from "./PlotTrialSection";

const getUpdatePlotGridError = getApiErrorMessage(
  "Failed to update plot grid.",
);

const getUpdateRowBoundariesError = getApiErrorMessage(
  "Failed to update row boundaries.",
);

const getClearPlotGridError = getApiErrorMessage("Failed to clear plot grid.");

const CLEAR_PLOT_GRID_SNIPS_TOOLTIP =
  "This trial has plot snips, so the plot grid can't be cleared.";

const defaultTrialRowSnips: RowSnipResponse[] = [];

interface DrawPlotGridPanelProps {}

const overlapWarningMessage = (
  showPlotOverlapWarning: boolean,
  showRowOverlapWarning: boolean,
): string | null => {
  if (showPlotOverlapWarning && showRowOverlapWarning) {
    return "Plots and rows overlap. Adjust the grid before saving.";
  }
  if (showPlotOverlapWarning) {
    return "Plots overlap. Adjust the grid before saving.";
  }
  if (showRowOverlapWarning) {
    return "Rows overlap. Adjust the grid before saving.";
  }
  return null;
};

const parsePositiveMeters = (value: string): number | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
};

const isEditableKeyboardTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName;
  return (
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT" ||
    target.isContentEditable
  );
};

const getGridLoadPendingLabel = (
  plotsInProgress: boolean,
  rowsInProgress: boolean,
): string | null => {
  if (plotsInProgress && rowsInProgress) {
    return "Loading plots and rows...";
  }
  if (plotsInProgress) {
    return "Loading plots...";
  }
  if (rowsInProgress) {
    return "Loading rows...";
  }
  return null;
};

const getGridLoadError = (
  plotsLoadFailed: boolean,
  rowsLoadFailed: boolean,
): string | null => {
  if (plotsLoadFailed && rowsLoadFailed) {
    return "Plots and rows failed to load";
  }
  if (plotsLoadFailed) {
    return "Plots failed to load";
  }
  if (rowsLoadFailed) {
    return "Rows failed to load";
  }
  return null;
};

const mergeTrialPlotsIntoLocation = (
  locationPlots: PlotGeoResponse[],
  trialId: number,
  trialPlots: PlotGeoResponse[],
): PlotGeoResponse[] => [
  ...locationPlots.filter((plot) => plot.trial_id !== trialId),
  ...trialPlots,
];

const DrawPlotGridPanel: React.FC<DrawPlotGridPanelProps> = () => {
  const { setAppDialog } = useAppDialog();
  const queryClient = useQueryClient();
  const { default: map } = useMap();
  const [prefs, isReady] = usePagePrefs();
  const {
    plot: {
      acceptSaved,
      canRedo,
      canUndo,
      cancelDraft,
      commit,
      current,
      enterPlotGridMode,
      growerGeoId: selectedGrowerId,
      isDirty,
      isPlotGridMode,
      plotsOverlap,
      redo,
      resetToInitialPlacement,
      rowsOverlap,
      selectGrower,
      selectSite,
      selectTrial,
      setDimensions,
      setPlotSize,
      siteGeoId: selectedSiteId,
      trialId: selectedTrialId,
      undo,
    },
  } = usePlotMap();
  const [plotLengthInput, setPlotLengthInput] = useState(
    String(DEFAULT_PLOT_LENGTH_METERS),
  );
  const [plotWidthInput, setPlotWidthInput] = useState(
    String(DEFAULT_PLOT_WIDTH_METERS),
  );
  const locationUuid = (prefs.location as string | null | undefined) ?? "";

  const plotsGeoParams = {
    location_uuid: locationUuid,
    trial_id: selectedTrialId ?? undefined,
  };
  const {
    data: plotsGeoResponse,
    isError: isPlotsError,
    isFetching: isPlotsFetching,
    isPending: isPlotsQueryPending,
  } = useGetPlotsGeoByLocation(plotsGeoParams, {
    query: {
      enabled: locationUuid !== "" && selectedTrialId != null,
    },
  });
  const isPlotsInProgress =
    selectedTrialId != null && (isPlotsQueryPending || isPlotsFetching);
  const plotsLoadFailed =
    selectedTrialId != null &&
    !isPlotsInProgress &&
    (isPlotsError ||
      (plotsGeoResponse != null && plotsGeoResponse.status !== 200));
  const plotsGeo =
    plotsGeoResponse?.status === 200 ? plotsGeoResponse.data : [];

  const { data: growers = [], isPending: isGrowersPending } =
    useGetGrowersGeoByLocation(
      { location_uuid: locationUuid },
      {
        query: {
          enabled: locationUuid !== "",
          select: (res): GrowerGeoResponse[] =>
            res.status === 200 ? res.data : [],
        },
      },
    );

  const { data: sites = [], isPending: isSitesPending } =
    useGetSitesGeoByLocation(
      { location_uuid: locationUuid },
      {
        query: {
          enabled: locationUuid !== "",
          select: (res): SiteGeoResponse[] =>
            res.status === 200 ? res.data : [],
        },
      },
    );

  const { data: trials = [], isPending: isTrialsPending } =
    useGetTrialsGeoByLocation(
      { location_uuid: locationUuid },
      {
        query: {
          enabled: locationUuid !== "",
          select: (res): TrialGeoResponse[] =>
            res.status === 200 ? res.data : [],
        },
      },
    );

  const growersWithBoundary = growers.filter((grower) =>
    hasBoundaryGeoJson(grower.boundary),
  );
  const hasGrowerBoundary = growersWithBoundary.length > 0;
  const hasSites = sites.length > 0;
  const isPrerequisitesPending =
    isGrowersPending || isSitesPending || isTrialsPending;

  const selectedTrial =
    trials.find((trial) => trial.id === selectedTrialId) ?? null;
  const {
    data: rowsGeoResponse,
    isError: isRowsError,
    isFetching: isRowsFetching,
    isLoading: isTrialRowsLoading,
    isPending: isRowsQueryPending,
  } = useGetRowsGeo(
    { trial_id: selectedTrialId ?? undefined },
    {
      query: {
        enabled: selectedTrialId != null,
      },
    },
  );
  const isRowsInProgress =
    selectedTrialId != null && (isRowsQueryPending || isRowsFetching);
  const rowsLoadFailed =
    selectedTrialId != null &&
    !isRowsInProgress &&
    (isRowsError ||
      (rowsGeoResponse != null && rowsGeoResponse.status !== 200));
  const trialRows = rowsGeoResponse?.status === 200 ? rowsGeoResponse.data : [];
  const gridLoadPendingLabel = getGridLoadPendingLabel(
    isPlotsInProgress,
    isRowsInProgress,
  );
  const gridLoadError = getGridLoadError(plotsLoadFailed, rowsLoadFailed);
  const selectedSite = sites.find((site) => site.id === selectedSiteId) ?? null;

  const { isPending: isUpdating, mutateAsync: updatePlotsGeoBulk } =
    useUpdatePlotsGeoBulk();
  const { isPending: isUpdatingLayout, mutateAsync: updatePlotsGeoLayoutBulk } =
    useUpdatePlotsGeoLayoutBulk();
  const { isPending: isUpdatingRows, mutateAsync: updateRowsGeoBulk } =
    useUpdateRowsGeoBulk();
  const { isPending: isDeleting, mutateAsync: deletePlotGeo } =
    useDeletePlotGeo();
  const { isPending: isClearingPlotGrid, mutateAsync: resetPlotsGeoLayout } =
    useResetPlotsGeoLayout();
  const isSaving =
    isUpdating || isUpdatingLayout || isUpdatingRows || isDeleting;

  const hasPlotRecords = plotsGeo.length > 0;
  const hasServerPlots = hasPlotsGeo(plotsGeo);
  const { data: trialRowSnips = defaultTrialRowSnips } = useGetRowSnips(
    { trial_id: selectedTrialId ?? undefined },
    {
      query: {
        enabled: selectedTrialId != null && hasServerPlots,
        select: (res): RowSnipResponse[] =>
          res.status === 200 ? res.data : defaultTrialRowSnips,
      },
    },
  );
  const clearPlotGridDisabledReason =
    trialRowSnips.length > 0 ? CLEAR_PLOT_GRID_SNIPS_TOOLTIP : null;
  const showPlotSizeControls =
    isPlotGridMode && current != null && !current.isCommitted;
  const trialPlotSizeInfo = formatTrialPlotSizeInfo(selectedTrial);
  const parsedPlotLength = parsePositiveMeters(plotLengthInput);
  const parsedPlotWidth = parsePositiveMeters(plotWidthInput);
  const canEditPlotSize = showPlotSizeControls && !isSaving;
  const trialLayout = getInitialGridLayout(plotsGeo, selectedTrial);
  const canResetToTrialPlotSize =
    canEditPlotSize &&
    trialLayout != null &&
    current?.plotLengthMeters != null &&
    current?.plotWidthMeters != null &&
    (Math.abs(current.plotLengthMeters - trialLayout.plotLengthMeters) >
      0.001 ||
      Math.abs(current.plotWidthMeters - trialLayout.plotWidthMeters) > 0.001);
  // Save only after Commit (or when hydrated from the server as committed).
  const isGridCommitted = current?.isCommitted === true;
  const hasValidDimensions =
    hasServerPlots ||
    (current?.rows != null &&
      current?.columns != null &&
      parsedPlotLength != null &&
      parsedPlotWidth != null);
  const canUseGridHistory = isPlotGridMode && current != null;
  const cellCount = current?.mesh?.cells.length ?? 0;
  const { areaHectares, plotCount } = getPlotGridStats(current);
  const hasPlotsOutsideSite = hasPlotGridOutsideSiteBoundary(
    current,
    selectedSite?.boundary,
  );
  const showPlotOverlapWarning =
    isPlotGridMode && isGridCommitted && plotsOverlap;
  const showRowOverlapWarning =
    isPlotGridMode && isGridCommitted && rowsOverlap;
  const overlapWarning = overlapWarningMessage(
    showPlotOverlapWarning,
    showRowOverlapWarning,
  );
  const isGridLayerHidden =
    isPlotGridMode && !isPlotMapLayerVisible(prefs, "grid");
  const areaDisplay =
    areaHectares != null ? `${areaHectares.toFixed(2)} ha` : "--";
  const canSave =
    isDirty &&
    isGridCommitted &&
    cellCount > 0 &&
    !hasPlotsOutsideSite &&
    overlapWarning == null &&
    !isSaving &&
    !isTrialRowsLoading &&
    locationUuid !== "" &&
    selectedTrialId != null;
  const canCommit =
    current != null &&
    !current.isCommitted &&
    !hasPlotsOutsideSite &&
    !isSaving &&
    !isTrialRowsLoading;

  const showNoPlotsState =
    locationUuid !== "" &&
    selectedTrialId != null &&
    !isPlotsQueryPending &&
    !plotsLoadFailed &&
    !hasPlotRecords &&
    !isPlotGridMode &&
    !isDirty &&
    current == null;

  const showEmptyState =
    locationUuid !== "" &&
    !isPlotsQueryPending &&
    hasPlotRecords &&
    !hasServerPlots &&
    !isPlotGridMode &&
    !isDirty &&
    current == null;

  const showDisplayState =
    !isPlotGridMode &&
    !isDirty &&
    (hasServerPlots || (isGridCommitted && cellCount > 0));

  useEffect(() => {
    if (
      !isReady ||
      isPrerequisitesPending ||
      isDirty ||
      isPlotGridMode ||
      isSaving
    ) {
      return;
    }

    const growerId = readSelectedGrowerGeoId(
      prefs,
      growers.map((grower) => grower.id),
    );
    const siteId = readSelectedSiteGeoId(
      prefs,
      sites.map((site) => site.id),
    );
    const trialId = readSelectedTrialGeoId(
      prefs,
      trials.map((trial) => trial.id),
    );
    if (
      growerId === selectedGrowerId &&
      siteId === selectedSiteId &&
      trialId === selectedTrialId
    ) {
      return;
    }

    selectGrower(growerId);
    selectSite(siteId);
    selectTrial(trialId);
  }, [
    growers,
    isDirty,
    isPlotGridMode,
    isPrerequisitesPending,
    isReady,
    isSaving,
    prefs,
    selectedGrowerId,
    selectedSiteId,
    selectedTrialId,
    selectGrower,
    selectSite,
    selectTrial,
    sites,
    trials,
  ]);

  useEffect(() => {
    if (
      current == null ||
      current.rows == null ||
      current.columns == null ||
      current.plotLengthMeters == null ||
      current.plotWidthMeters == null
    ) {
      return;
    }

    setPlotLengthInput(String(current.plotLengthMeters));
    setPlotWidthInput(String(current.plotWidthMeters));
  }, [current]);

  useEffect(() => {
    if (!isPlotGridMode) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableKeyboardTarget(event.target) || isSaving) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        cancelDraft();
        return;
      }

      if (!canUseGridHistory) {
        return;
      }

      const hasModifier = event.metaKey || event.ctrlKey;
      if (!hasModifier || event.key.toLowerCase() !== "z") {
        return;
      }

      if (event.shiftKey) {
        if (!canRedo) {
          return;
        }
        event.preventDefault();
        redo();
        return;
      }

      if (!canUndo) {
        return;
      }
      event.preventDefault();
      undo();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    canRedo,
    canUndo,
    canUseGridHistory,
    cancelDraft,
    isPlotGridMode,
    isSaving,
    redo,
    undo,
  ]);

  const commitPlotSizeInput = (lengthValue: string, widthValue: string) => {
    if (!canEditPlotSize) {
      return;
    }

    const plotLengthMeters = parsePositiveMeters(lengthValue);
    const plotWidthMeters = parsePositiveMeters(widthValue);
    if (plotLengthMeters == null || plotWidthMeters == null) {
      if (
        current?.plotLengthMeters != null &&
        current.plotWidthMeters != null
      ) {
        setPlotLengthInput(String(current.plotLengthMeters));
        setPlotWidthInput(String(current.plotWidthMeters));
      }
      return;
    }

    if (current.plotLengthMeters == null || current.plotWidthMeters == null) {
      return;
    }

    if (
      Math.abs(plotLengthMeters - current.plotLengthMeters) < 0.0001 &&
      Math.abs(plotWidthMeters - current.plotWidthMeters) < 0.0001
    ) {
      setPlotLengthInput(String(plotLengthMeters));
      setPlotWidthInput(String(plotWidthMeters));
      return;
    }

    setPlotSize(plotLengthMeters, plotWidthMeters);
  };

  const handlePlotLengthChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPlotLengthInput(event.target.value);
  };

  const handlePlotWidthChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPlotWidthInput(event.target.value);
  };

  const handlePlotSizeBlur = () => {
    commitPlotSizeInput(plotLengthInput, plotWidthInput);
  };

  const blurPlotSizeInputOnEnter = (
    event: ReactKeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    event.currentTarget.blur();
  };

  const showSaveErrorDialog = (message: string) => {
    setAppDialog({
      confirmText: "Close",
      message: <>{message}</>,
      onConfirm: noop,
      open: true,
      title: "Error",
    });
  };

  const syncPlotsGeoCache = (nextTrialPlots: PlotGeoResponse[]) => {
    if (locationUuid === "" || selectedTrialId == null) {
      return;
    }

    const writePlotsGeoCache = (
      params: { location_uuid: string; trial_id?: number },
      plots: PlotGeoResponse[],
    ) => {
      queryClient.setQueryData<getPlotsGeoByLocationResponse>(
        getGetPlotsGeoByLocationQueryKey(params),
        (previous) => {
          if (previous == null) {
            return {
              data: plots,
              headers: new Headers(),
              status: 200,
            };
          }

          if (previous.status !== 200) {
            return previous;
          }

          return {
            ...previous,
            data: plots,
          };
        },
      );
    };

    writePlotsGeoCache(plotsGeoParams, nextTrialPlots);

    queryClient.setQueryData<getPlotsGeoByLocationResponse>(
      getGetPlotsGeoByLocationQueryKey({ location_uuid: locationUuid }),
      (previous) => {
        const locationPlots = previous?.status === 200 ? previous.data : [];
        const merged = mergeTrialPlotsIntoLocation(
          locationPlots,
          selectedTrialId,
          nextTrialPlots,
        );

        if (previous == null) {
          return {
            data: merged,
            headers: new Headers(),
            status: 200,
          };
        }

        if (previous.status !== 200) {
          return previous;
        }

        return {
          ...previous,
          data: merged,
        };
      },
    );
  };

  const syncRowsGeoCache = (updatedRows: RowGeoResponse[]) => {
    if (updatedRows.length === 0) {
      return;
    }

    const updatedById = new Map(updatedRows.map((row) => [row.id, row]));
    const writeRowsGeoCache = (params: {
      location_uuid?: string;
      trial_id?: number;
    }) => {
      queryClient.setQueryData<getRowsGeoResponse>(
        getGetRowsGeoQueryKey(params),
        (previous) => {
          if (previous == null || previous.status !== 200) {
            return previous;
          }

          return {
            ...previous,
            data: previous.data.map((row) => updatedById.get(row.id) ?? row),
          };
        },
      );
    };

    // The map caches rows by location; this panel caches them by trial.
    // Both must receive the saved boundaries or the map reloads the old dividers.
    if (locationUuid !== "") {
      writeRowsGeoCache({ location_uuid: locationUuid });
    }
    if (selectedTrialId != null) {
      writeRowsGeoCache({ trial_id: selectedTrialId });
    }
  };

  const handleStartDrawing = () => {
    const layout = getInitialGridLayout(plotsGeo, selectedTrial);
    if (layout == null) {
      return;
    }

    const { columns, plotLengthMeters, plotWidthMeters, rows } = layout;
    const cellSeeds = getInitialCellSeeds(plotsGeo, selectedTrial);

    setPlotLengthInput(String(plotLengthMeters));
    setPlotWidthInput(String(plotWidthMeters));

    enterPlotGridMode();
    setDimensions(rows, columns, cellSeeds);
    setPlotSize(plotLengthMeters, plotWidthMeters);
    fitMapToBoundary(map, selectedSite?.boundary);
  };

  const handleEditPlotGrid = () => {
    enterPlotGridMode();
    fitMapToBoundaries(
      map,
      plotsGeo.map((plot) => plot.boundary),
    );
  };

  const handleConfirmClearPlotGrid = async () => {
    if (selectedTrialId == null || locationUuid === "" || isClearingPlotGrid) {
      return;
    }

    try {
      const response = await resetPlotsGeoLayout({
        data: { trial_id: selectedTrialId },
      });
      if (response.status !== 200) {
        showSaveErrorDialog(getClearPlotGridError(response.data));
        return;
      }

      const returnedPlots = response.data.map((result) => result.plot);
      const updatedById = new Map(returnedPlots.map((plot) => [plot.id, plot]));
      const knownPlotIds = new Set(plotsGeo.map((plot) => plot.id));
      const nextPlots = [
        ...plotsGeo.map((plot) => updatedById.get(plot.id) ?? plot),
        ...returnedPlots.filter((plot) => !knownPlotIds.has(plot.id)),
      ];
      syncPlotsGeoCache(nextPlots);
      syncRowsGeoCache(response.data.flatMap((result) => result.rows));

      const layout = getInitialGridLayout(nextPlots, selectedTrial);
      if (layout == null) {
        return;
      }

      const { columns, plotLengthMeters, plotWidthMeters, rows } = layout;
      setPlotLengthInput(String(plotLengthMeters));
      setPlotWidthInput(String(plotWidthMeters));
      resetToInitialPlacement(
        rows,
        columns,
        plotLengthMeters,
        plotWidthMeters,
        getInitialCellSeeds(nextPlots, selectedTrial),
      );
      fitMapToBoundary(map, selectedSite?.boundary);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to clear plot grid.";
      showSaveErrorDialog(message);
    }
  };

  const handleRequestClearPlotGrid = () => {
    if (
      selectedTrialId == null ||
      !hasServerPlots ||
      isClearingPlotGrid ||
      clearPlotGridDisabledReason != null
    ) {
      return;
    }

    setAppDialog({
      cancelText: "Cancel",
      confirmText: "Clear plot grid",
      message: (
        <>
          This will clear all plot and row geometry, requiring you to place the
          trial again. This cannot be undone.
        </>
      ),
      onCancel: noop,
      onConfirm: () => {
        void handleConfirmClearPlotGrid();
      },
      open: true,
      title: "Clear plot grid?",
    });
  };

  const handleResetToTrialPlotSize = () => {
    if (trialLayout == null || !canEditPlotSize) {
      return;
    }

    const { plotLengthMeters, plotWidthMeters } = trialLayout;
    setPlotLengthInput(String(plotLengthMeters));
    setPlotWidthInput(String(plotWidthMeters));
    setPlotSize(plotLengthMeters, plotWidthMeters);
  };

  const handleCommitGrid = () => {
    if (!canCommit) {
      return;
    }

    commit(undefined, trialRows);
  };

  const handleSavePlotGrid = async () => {
    if (
      !canSave ||
      current?.mesh == null ||
      locationUuid === "" ||
      hasPlotsOutsideSite
    ) {
      return;
    }

    try {
      if (current.mesh.cells.length === 0) {
        showSaveErrorDialog(
          "Plot grid must have at least one cell before saving.",
        );
        return;
      }

      const plan = planPlotGridEditSave(current.mesh, plotsGeo, trialRows);
      if (plan.creates.length > 0) {
        showSaveErrorDialog(
          "Plot grid includes cells that do not match existing plot records.",
        );
        return;
      }

      // Cache updates stay in this sequence rather than mutation onSuccess.
      // These updates resolve on non-200 responses, and the plot and row caches
      // are merged across several requests. Writing either cache early lets the
      // map reload a half-saved grid before acceptSaved().
      let nextPlots = [...plotsGeo];
      const updatedRows: RowGeoResponse[] = [];

      if (plan.layoutUpdates.length > 0) {
        console.info(
          "[DrawPlotGridPanel] sending plot layout updates",
          JSON.stringify({
            plots: plan.layoutUpdates.length,
            rows: plan.layoutUpdates.reduce(
              (total, item) => total + item.rows.length,
              0,
            ),
          }),
        );
        const response = await updatePlotsGeoLayoutBulk({
          data: { plots: plan.layoutUpdates },
        });
        if (response.status !== 200) {
          showSaveErrorDialog(getUpdatePlotGridError(response.data));
          return;
        }
        const updatedById = new Map(
          response.data.map((result) => [result.plot.id, result.plot]),
        );
        nextPlots = nextPlots.map((plot) => updatedById.get(plot.id) ?? plot);
        syncPlotsGeoCache(nextPlots);
        response.data.forEach((result) => {
          updatedRows.push(...result.rows);
        });
      }

      if (plan.plotUpdates.length > 0) {
        console.info(
          "[DrawPlotGridPanel] sending plot updates",
          JSON.stringify({ plots: plan.plotUpdates.length }),
        );
        const response = await updatePlotsGeoBulk({
          data: { plots: plan.plotUpdates },
        });
        if (response.status !== 200) {
          showSaveErrorDialog(getUpdatePlotGridError(response.data));
          return;
        }
        const updatedById = new Map(
          response.data.map((plot) => [plot.id, plot]),
        );
        nextPlots = nextPlots.map((plot) => updatedById.get(plot.id) ?? plot);
        syncPlotsGeoCache(nextPlots);
      }

      for (const plotGeoId of plan.deletes) {
        const response = await deletePlotGeo({ plotGeoId });
        if (response.status !== 200) {
          showSaveErrorDialog(getUpdatePlotGridError(response.data));
          return;
        }
        nextPlots = nextPlots.filter((plot) => plot.id !== plotGeoId);
        syncPlotsGeoCache(nextPlots);
      }

      if (plan.rowUpdates.length > 0) {
        console.info(
          "[DrawPlotGridPanel] sending row updates",
          JSON.stringify({ rows: plan.rowUpdates.length }),
        );
        const response = await updateRowsGeoBulk({
          data: { rows: plan.rowUpdates },
        });
        if (response.status !== 200) {
          showSaveErrorDialog(getUpdateRowBoundariesError(response.data));
          return;
        }
        updatedRows.push(...response.data);
      }

      syncRowsGeoCache(updatedRows);
      if (updatedRows.length > 0) {
        await queryClient.invalidateQueries({
          queryKey: getGetRowsGeoQueryKey(),
        });
      }

      acceptSaved();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to save plot grid.";
      showSaveErrorDialog(message);
    }
  };

  let statusMessage: string;
  if (hasServerPlots) {
    statusMessage = `Editing plot grid (${cellCount} cells). Select cells to drag; drag edges and vertices to slide, Shift+drag them to move; Shift+drag empty space for a selection box; Ctrl-drag (Cmd-drag on Mac) a corner to rotate contiguous cells. Press ESC to cancel.`;
  } else if (!hasValidDimensions) {
    statusMessage = "Enter positive plot length and width in meters.";
  } else if (current == null) {
    statusMessage = "Creating a plot grid at the map location.";
  } else if (current.isCommitted) {
    statusMessage = `Grid committed (${cellCount} cells). Select cells to drag; drag edges and vertices to slide, Shift+drag them to move; Shift+drag empty space for a selection box; Ctrl-drag (Cmd-drag on Mac) a corner to rotate contiguous cells. Press ESC to cancel.`;
  } else {
    statusMessage = `Grid ready (${current.rows}×${current.columns}, ${current.plotLengthMeters} m × ${current.plotWidthMeters} m plots). Drag the grid to move it, a corner to reshape it, or Ctrl-drag (Cmd-drag on Mac) a corner to rotate. Press ESC to cancel.`;
  }

  let gridContent: ReactNode;
  if (isPlotsInProgress && !showDisplayState) {
    gridContent = (
      <Stack spacing={1}>
        <Alert role="status" severity="info">
          {gridLoadPendingLabel}
        </Alert>
        <Stack direction="row" spacing={1}>
          <Button
            aria-label="Edit plot grid"
            disabled
            size="small"
            variant="text"
          >
            Edit Plot Grid
          </Button>
        </Stack>
      </Stack>
    );
  } else if (
    plotsLoadFailed &&
    !hasServerPlots &&
    !isPlotGridMode &&
    !isDirty &&
    current == null
  ) {
    gridContent = (
      <Stack spacing={1}>
        {gridLoadPendingLabel != null && (
          <Alert role="status" severity="info">
            {gridLoadPendingLabel}
          </Alert>
        )}
        <Alert role="alert" severity="error">
          {gridLoadError}
        </Alert>
        <Stack direction="row" spacing={1}>
          <Button
            aria-label="Edit plot grid"
            disabled
            size="small"
            variant="text"
          >
            Edit Plot Grid
          </Button>
        </Stack>
      </Stack>
    );
  } else if (showNoPlotsState) {
    gridContent = (
      <Stack spacing={0.5}>
        <Typography
          component="h3"
          sx={{ fontWeight: "bold" }}
          variant="subtitle2"
        >
          No Plots Found
        </Typography>
        <Typography color="error" variant="body2">
          This trial has no plots. Upload plots for this trial before drawing a
          plot grid.
        </Typography>
      </Stack>
    );
  } else if (showEmptyState) {
    gridContent = (
      <>
        {gridLoadPendingLabel != null && (
          <Alert role="status" severity="info">
            {gridLoadPendingLabel}
          </Alert>
        )}
        {rowsLoadFailed && (
          <Alert role="alert" severity="error">
            {gridLoadError}
          </Alert>
        )}
        <Typography color="text.secondary" variant="body2">
          No plot grid yet. Draw one on the map to define plot boundaries.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            aria-label="Draw plot grid"
            disabled={gridLoadPendingLabel != null || gridLoadError != null}
            onClick={handleStartDrawing}
            size="small"
            variant="text"
          >
            Draw Plot Grid
          </Button>
        </Stack>
      </>
    );
  } else if (showDisplayState) {
    const displayCount =
      plotsGeo.filter((plot) => (plot.boundary?.coordinates.length ?? 0) > 0)
        .length || cellCount;

    gridContent = (
      <>
        <Typography color="text.secondary" variant="body2">
          Plot grid is saved and shown on the map.
        </Typography>
        <Typography variant="body1">
          {displayCount} plot{displayCount === 1 ? "" : "s"}
        </Typography>
        {gridLoadPendingLabel != null && (
          <Alert role="status" severity="info">
            {gridLoadPendingLabel}
          </Alert>
        )}
        {gridLoadError != null && (
          <Alert role="alert" severity="error">
            {gridLoadError}
          </Alert>
        )}
        <Stack direction="row" spacing={1}>
          <Button
            aria-label="Edit plot grid"
            disabled={gridLoadPendingLabel != null || gridLoadError != null}
            onClick={handleEditPlotGrid}
            size="small"
            variant="text"
          >
            Edit Plot Grid
          </Button>
        </Stack>
      </>
    );
  } else {
    gridContent = (
      <>
        {isGridLayerHidden && (
          <Alert role="alert" severity="warning">
            Plot Grid layer is hidden. Show it to see the grid you're editing on
            the map.
          </Alert>
        )}
        {showPlotSizeControls && (
          <Stack spacing={1}>
            {trialPlotSizeInfo != null && (
              <Typography color="text.secondary" variant="body2">
                {trialPlotSizeInfo}
              </Typography>
            )}
            <Stack direction="row" spacing={1.5}>
              <TextField
                aria-label="Plot length in meters"
                disabled={!canEditPlotSize}
                fullWidth
                label="Plot length (m)"
                onBlur={handlePlotSizeBlur}
                onChange={handlePlotLengthChange}
                size="small"
                slotProps={{
                  htmlInput: {
                    min: 0,
                    onKeyDown: blurPlotSizeInputOnEnter,
                    step: 0.1,
                  },
                }}
                type="number"
                value={plotLengthInput}
              />
              <TextField
                aria-label="Plot width in meters"
                disabled={!canEditPlotSize}
                fullWidth
                label="Plot width (m)"
                onBlur={handlePlotSizeBlur}
                onChange={handlePlotWidthChange}
                size="small"
                slotProps={{
                  htmlInput: {
                    min: 0,
                    onKeyDown: blurPlotSizeInputOnEnter,
                    step: 0.1,
                  },
                }}
                type="number"
                value={plotWidthInput}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                aria-label="Reset to trial settings"
                disabled={!canResetToTrialPlotSize}
                onClick={handleResetToTrialPlotSize}
                size="small"
                variant="text"
              >
                Reset to trial settings
              </Button>
            </Stack>
          </Stack>
        )}
        <Stack direction="row" spacing={2}>
          <Stack aria-live="polite" direction="column" spacing={0}>
            <Typography variant="caption">Plots</Typography>
            <Typography variant="body1">{plotCount}</Typography>
          </Stack>
          <Stack aria-live="polite" direction="column" spacing={0}>
            <Typography variant="caption">Grid Area</Typography>
            <Typography variant="body1">{areaDisplay}</Typography>
          </Stack>
        </Stack>
        <Typography color="text.secondary" variant="body2">
          {statusMessage}
        </Typography>
        {hasPlotsOutsideSite && (
          <Alert role="alert" severity="warning">
            {isGridCommitted
              ? "One or more plots are outside the site boundary. Adjust the grid before saving."
              : "The plot grid is outside the site boundary. Adjust the grid before committing."}
          </Alert>
        )}
        {overlapWarning != null && (
          <Alert role="alert" severity="warning">
            {overlapWarning}
          </Alert>
        )}
        <Stack direction="row" spacing={1}>
          {canUseGridHistory && (
            <>
              <Button
                aria-label="Undo plot grid edit"
                disabled={!canUndo || isSaving}
                onClick={undo}
                size="small"
                startIcon={<Undo />}
                variant="text"
              >
                Undo
              </Button>
              <Button
                aria-label="Redo plot grid edit"
                disabled={!canRedo || isSaving}
                onClick={redo}
                size="small"
                startIcon={<Redo />}
                variant="text"
              >
                Redo
              </Button>
            </>
          )}
          <Button
            aria-label="Commit plot grid"
            disabled={!canCommit}
            onClick={handleCommitGrid}
            size="small"
            variant="text"
          >
            Commit
          </Button>
          {hasServerPlots && (
            <Tooltip title={clearPlotGridDisabledReason ?? ""}>
              <span style={{ display: "inline-flex" }}>
                <Button
                  aria-label="Clear plot grid"
                  color="error"
                  disabled={
                    isSaving ||
                    isClearingPlotGrid ||
                    clearPlotGridDisabledReason != null
                  }
                  loading={isClearingPlotGrid}
                  onClick={handleRequestClearPlotGrid}
                  size="small"
                  variant="text"
                >
                  Clear
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            aria-label="Cancel plot grid edit"
            color="error"
            disabled={isSaving}
            onClick={cancelDraft}
            size="small"
            variant="text"
          >
            Cancel
          </Button>
          <Button
            aria-label="Save plot grid"
            disabled={!canSave}
            loading={isSaving}
            onClick={() => {
              void handleSavePlotGrid();
            }}
            size="small"
            variant="text"
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </>
    );
  }

  let panelContent: ReactNode;
  if (locationUuid !== "" && isPrerequisitesPending) {
    panelContent = (
      <CircularProgress aria-label="Loading plot grid" size={24} />
    );
  } else if (!hasGrowerBoundary) {
    panelContent = (
      <GoToBoundsPrompt
        header="No Grower Boundaries Found"
        message="There are no grower boundaries for this location. Please add one and associate the relevant sites."
      />
    );
  } else if (!hasSites) {
    panelContent = (
      <GoToBoundsPrompt
        header="No Sites Found"
        message="There are no sites for this location. Please associate the relevant sites to their grower boundaries."
      />
    );
  } else {
    panelContent = selectedTrialId != null ? gridContent : null;
  }

  return (
    <PlotTrialSection
      isDisabled={isPlotGridMode || isDirty || isSaving}
      sites={sites}
      spacing={1.5}
      title="Plots"
      trials={trials}
    >
      {panelContent}
    </PlotTrialSection>
  );
};

export default DrawPlotGridPanel;
